import { createListenerMiddleware } from '@reduxjs/toolkit';
import { Call, Device } from '@twilio/voice-sdk';
import TwilioError from '@twilio/voice-sdk/es5/twilio/errors/twilioError';
import { toE164 } from '../../sic-lib/utils/twilioHelpers';
import { getMedia, serializeTwilioCall, serializeTwilioDevice } from '../../utils/twilioHelpers';
import {
    acceptCall,
    bootstrap,
    callConnected,
    callDisconnected,
    callRinging,
    deviceChangeAudio,
    deviceError,
    deviceIncoming,
    deviceRegistered,
    deviceRegistering,
    deviceUnregistered,
    dispose,
    endCall,
    muteCall,
    requestTwilioConfigThunk,
    sendDigits,
    startCall,
} from '../dialer';
import { AppDispatch, RootState } from '../store';

//track the device and active call
let device: Device | undefined;
let activeCall: Call | undefined;

//subscribe to device's call events
//NOTE: All call related middleware dispatches are prefixed with "call"
//Ex: callConnected, callDisconnected, etc
const subscribeActiveCall = (dispatch: AppDispatch) => {
    if (!activeCall) return;

    //console.log('[TM].[ACE]: Subscribing to call events', activeCall.toString());

    activeCall.on('accept', call => {
        //console.log('[TM].[ACE]: Call accepted', call.toString());
        dispatch(
            callConnected({
                twilioDevice: serializeTwilioDevice(device),
                twilioCall: serializeTwilioCall(call!),
                callStart: new Date().toISOString(),
            }),
        );
    });

    activeCall.on('cancel', () => {
        //console.log('[TM].[ACE]: Call cancelled');
        dispatch(callDisconnected(serializeTwilioDevice(device)));
        activeCall = undefined;
    });

    activeCall.on('disconnect', () => {
        //console.log('[TM].[ACE]: Call disconnected');
        dispatch(callDisconnected(serializeTwilioDevice(device)));
        activeCall = undefined;
    });

    activeCall.on('ringing', () => {
        //console.log('[TM].[ACE]: Call ringing');
        dispatch(
            callRinging({
                twilioDevice: serializeTwilioDevice(device),
                twilioCall: serializeTwilioCall(activeCall!),
            }),
        );
    });
};

//create the listener middleware with appropriate types
export const dialerListenerMiddleware = createListenerMiddleware();
export const startDialerListening = dialerListenerMiddleware.startListening.withTypes<
    RootState,
    AppDispatch
>();

//initialize kicks: loading twilio config/token, register device events, request mic permissions
startDialerListening({
    actionCreator: bootstrap,
    effect: (action, { dispatch, getState }) => {
        //console.log('[TM]: Bootstrap');
        dispatch(requestTwilioConfigThunk());
    },
});

//setup the device and listener
//NOTE: All device related middleware dispatches are prefixed with "device".
//Ex: deviceRegistered, deviceIncoming, etc
startDialerListening({
    actionCreator: requestTwilioConfigThunk.fulfilled,
    effect: (action, { dispatch, getState }) => {
        //in the event of a token refresh, update the device token if its already setup
        if (!!device) {
            device.updateToken(action.payload.token);
            return;
        }

        device = new Device(action.payload.token);

        device.on('registering', () => {
            //console.log('[TM].[TD]: Device registering');
            dispatch(deviceRegistering(serializeTwilioDevice(device)));
        });

        device.on('registered', () => {
            //console.log('[TM].[TD]: Device registered');
            dispatch(deviceRegistered(serializeTwilioDevice(device)));
        });

        device.on('unregistered', () => {
            //console.log('[TM].[TD]: Device unregistered');
            dispatch(deviceUnregistered(serializeTwilioDevice(device)));
        });

        device.on('error', (twilioError: TwilioError, call: Call) => {
            //console.log('[TM].[TD]: Device Error', twilioError, call);
            dispatch(deviceError(twilioError));
        });

        device.on('incoming', (call: Call) => {
            //console.log('[TM].[TD]: Incoming call', call);
            //console.log('[TM].[TD]: Incoming device', device);

            activeCall = call;
            subscribeActiveCall(dispatch);

            dispatch(
                deviceIncoming({
                    twilioDevice: serializeTwilioDevice(device),
                    twilioCall: serializeTwilioCall(call),
                }),
            );
        });

        //Emitted when the Device instance's AccessToken is about to expire.
        device.on('tokenWillExpire', async () => {
            //console.log('[TM].[TD]: tokenWillExpire');
            dispatch(requestTwilioConfigThunk());
        });

        /*
            Fired when a change in the available audio inputs is observed; fired when the getUserMedia
            prompt resolves successfuly (adding input devices to the list of available input devices).

            Update the serialized device held in the Dialer store to show that the user has input devices,
            no input devices should display some sort of error. No available input devices means the user
            will not be able to accept incoming or outgoing calls.
         */
        device.audio?.on('deviceChange', function () {
            //console.log('[TM].[TD]: device audio change');
            dispatch(deviceChangeAudio(serializeTwilioDevice(device)));
        });

        /* 
            Prompt the user for permisson to use the microphone in browser tab

            If the user has already given permissions and that setting is cached in the browser this will
            be ignored and the 'device ready' event's serialized twilio device will show the available input devices,
            otherwise if the user is prompted and selects 'accept' this will trigger the 'device change' event 
            which will update the serialzed twilio device in the Dialer store.

            IMPORTANT: this should happen at the end of the setup process
         */
        getMedia({ audio: true, video: false });

        device.register();
    },
});

startDialerListening({
    actionCreator: startCall,
    effect: async (action, { dispatch, getState }) => {
        if (device) {
            //console.log('[TM].[Action]: startCall', action.payload);

            let params = {
                phoneNumber: toE164(action.payload.phoneNumber),
                ContactId: action.payload.contactId.toString(),
                FirstName: action.payload.firstName,
                LastName: action.payload.lastName,
                callSource: 'web:outbound',
            };

            //start the call
            activeCall = await device.connect({ params });
            subscribeActiveCall(dispatch);

            dispatch(
                callConnected({
                    twilioDevice: serializeTwilioDevice(device),
                    twilioCall: serializeTwilioCall(activeCall),
                    callStart: new Date().toISOString(),
                }),
            );
        }
    },
});

startDialerListening({
    actionCreator: endCall,
    effect: (action, { dispatch, getState }) => {
        if (activeCall) {
            //console.log('[TM].[Action]: endCall', action.payload);
            if (activeCall?.status() === 'pending') {
                activeCall?.ignore();
            } else {
                activeCall?.disconnect();
            }
        }
    },
});

startDialerListening({
    actionCreator: acceptCall,
    effect: (action, { dispatch, getState }) => {
        //console.log('[TM].[Action]: acceptCall', action.payload);
        if (activeCall?.status() === 'pending') {
            activeCall.accept();
        }
    },
});

startDialerListening({
    actionCreator: sendDigits,
    effect: (action, { dispatch, getState }) => {
        //console.log('[TM].[Action]: sendDigits', action.payload);
        if (activeCall) {
            activeCall.sendDigits(action.payload);
        }
    },
});

startDialerListening({
    actionCreator: muteCall,
    effect: (action, { dispatch, getState }) => {
        //console.log('[TM].[Action]: muteCall', action.payload);
        if (activeCall) {
            activeCall.mute(action.payload);
        }
    },
});

startDialerListening({
    actionCreator: dispose,
    effect: (action, { dispatch, getState }) => {
        //console.log('[TM].[Action]: dispose', action.payload);
        if (device) {
            device.destroy();
            device = undefined;
        }
    },
});
